class HirePurchaseCalculator extends HTMLElement {
  constructor() {
    super();

    this.totalYears = null;
    this.paymentType = null;
    this.paymentFrequency = null;
    this.interestRate = 0;

    // event handlers
    this.handleOptionSelect = this.handleOptionSelect.bind(this);
    this.calculateHirePurchasePrice =
      this.calculateHirePurchasePrice.bind(this);
  }

  connectedCallback() {
    this.url = this.dataset.currentUrl;
    this.basePrice = Number(this.dataset.basePrice) || 0;
    this.docFee = Number(this.dataset.docFee) || 0;
    this.otpFee = Number(this.dataset.otpFee) || 0;

    // watch the variant button
    document.addEventListener("click", (event) => {
      const variantButton = event.target.closest(".variantButton");
      if (variantButton) {
        const variantUrl = variantButton.dataset.variantUrl || this.url;
        this.handleVariantChange(variantUrl);
      }
    });

    // setup event listeners
    this.bindEventListeners();
    // preset values
    this.handlePresetValues();
  }

  bindEventListeners() {
    // error output
    this.hirePurchaseError = this.querySelector("#hirePurchaseError");

    // rates output
    this.hirePurchaseRates = this.querySelector("#hirePurchaseRates");

    // handle option values
    this.optionSelect = this.querySelectorAll(".optionSelect");
    if (this.optionSelect) {
      this.optionSelect.forEach((select) => {
        select.addEventListener("change", this.handleOptionSelect);
      });
    }

    // calculate hp
    this.hirePurchaseBtn = this.querySelector("#hirePurchaseBtn");
    if (this.hirePurchaseBtn) {
      this.hirePurchaseBtn.addEventListener(
        "click",
        this.calculateHirePurchasePrice
      );
    }
  }

  async handleVariantChange(newUrl) {
    try {
      const response = await fetch(newUrl);
      if (!response.ok) throw new Error(`http error`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");
      const newHppCalculator = html.querySelector("hire-purchase-calculator");

      if (newHppCalculator) {
        this.innerHTML = newHppCalculator.innerHTML;
        this.dataset.basePrice = newHppCalculator.dataset.basePrice;

        this.basePrice = Number(this.dataset.basePrice) || 0;

        this.bindEventListeners();
        this.handlePresetValues();
      }
    } catch (error) {
      console.error("Fetch failed:", error);
    }
  }

  handleOptionSelect(event) {
    // get option value
    const targetValue = event.target.value;

    // get selected option
    const target = event.target;
    this.updateInterestRateFromTermSelect();

    // prevents default being parsed
    if (targetValue === "") {
      console.error("Error: Please select a valid term length.");
      return;
    }

    // allocate correct function for option select type
    const termType = event.target.dataset.type;
    switch (termType) {
      case "term-length":
        this.setTermYears(targetValue);
        break;
      case "payment-frequency":
        this.setPaymentFrequency(targetValue);
        break;
      default:
        console.error("Error: Invalid Option Select");
        return;
    }
  }

  updateInterestRateFromTermSelect() {
    const termSelect = this.querySelector(
      ".optionSelect[data-type='term-length']"
    );
    if (termSelect) {
      const option = termSelect.options[termSelect.selectedIndex];
      this.interestRate = Number(option.dataset.interestRate) || 0;
    }
  }

  setTermYears(targetValue) {
    // set term years
    const termYears = Number(targetValue);
    if (termYears && termYears > 0) {
      this.totalYears = termYears;
    }
  }

  setPaymentFrequency(targetValue) {
    this.paymentType = targetValue;
    switch (this.paymentType) {
      case "weekly":
        this.paymentFrequency = 52;
        break;
      case "monthly":
        this.paymentFrequency = 12;
        break;
    }
  }

  handlePresetValues() {
    const selectedOption = this.querySelector(
      ".optionSelect[data-type='term-length'"
    );
    const termLengthOption =
      parseInt(
        this.querySelector(".optionSelect[data-type='term-length'")[1].value
      ) || 3;
    const paymentFrequencyOption =
      parseInt(
        this.querySelector(".optionSelect[data-type='payment-frequency'")[1]
          .value
      ) || 12;
    this.totalYears = termLengthOption;
    this.paymentFrequency = paymentFrequencyOption;
    this.paymentType = paymentFrequencyOption === 12 ? "monthly" : "weekly";
    this.updateInterestRateFromTermSelect();
    this.calculateHirePurchasePrice();
  }

  calculateHirePurchasePrice() {
    // remove previous text from hirePurchaseRates
    this.hirePurchaseError.innerHTML = "";

    const hasTotalYears = this.totalYears && this.totalYears > 0;
    const hasFrequency = this.paymentFrequency && this.paymentFrequency > 0;

    if (!hasTotalYears || !hasFrequency) {
      console.error("Error: Missing Terms and Frequency");
      this.hirePurchaseError.innerHTML = `<p class="text-primaryRed">Missing Terms and/or Frequency</p>`;
      return;
    }

    // calculate interest rate
    const totalInterest =
      this.basePrice * (this.interestRate / 100) * this.totalYears;
    // calculate total amount financed
    const amountFinanced = this.basePrice + totalInterest;
    // calculation payment frequency
    const rollingPayments =
      amountFinanced / (this.totalYears * this.paymentFrequency);

    // set params
    const paymentFrequency = this.totalYears * this.paymentFrequency;
    const paymentType = this.paymentType;
    const payments = parseFloat(rollingPayments).toFixed(2);
    const totalPayableAmount = parseFloat(amountFinanced).toFixed(2);

    this.hirePurchaseRates.innerHTML = hirePurchaseTemplate(
      paymentFrequency,
      paymentType,
      payments,
      totalPayableAmount,
      this.docFee,
      this.otpFee
    );
  }
}

document.addEventListener("DOMContentLoaded", () => {
  if (!customElements.get("hire-purchase-calculator")) {
    customElements.define("hire-purchase-calculator", HirePurchaseCalculator);
  }
});

function hirePurchaseTemplate(
  termDuration,
  termType,
  payments,
  totalPayableAmount,
  docFee,
  otpFee
) {
  return `
    <table class="w-full">
      <thead class="grid grid-cols-1">
        <tr class="tr mb-2 w-full">
          <th>${termDuration} ${termType} Payments of</th>
          <td>£${payments}</td>
        </tr>
      </thead>

      <tbody class="grid grid-cols-2 gap-2">
        <tr class="tr">
          <th>Term ${termType}</th>
          <td>${termDuration}</td>
        </tr>

        <tr class="tr">
          <th>Total Payable Amount</th>
          <td>£${totalPayableAmount}</td>
        </tr>

        <tr class="tr">
          <th>DOC Fee</th>
          <td>£${docFee}</td>
        </tr>

        <tr class="tr">
          <th>OTP Fee</th>
          <td>£${otpFee}</td>
        </tr>

      </tbody>
    </table>
  `;
}
